import React, { FC } from 'react'
import { Redirect, useLocation } from 'react-router'

import { useFlag, routes } from '@sketch/modules-common'

// Utils
import { ErrorHandler } from '@sketch/tracing'
import { getCanAdministerShares } from 'modules/shares/utils'

// Components
import { DocumentHead, Pill, useModalContext } from '@sketch/components'
import DocumentItemsLayout from 'modules/shares/components/DocumentItemsLayout'
import { DocumentItemDropdown } from 'modules/shares/components/DocumentItemDropdown'
import LoadingState from 'modules/shares/components/LoadingState'
import { ShareTrashedModal } from 'modules/shares/components/ShareTrashedModal'
import CollectionDropdown from 'modules/collections/components/CollectionDropdown'
import CollectionItemsLayout from 'modules/collections/components/CollectionItemsLayout'

// Local Components
import ProjectHeader from '../ProjectHeader'
import ProjectEmptyState from '../ProjectEmptyState'
import ProjectCollectionError from '../ProjectCollectionError'
import { StyledHeader, DocumentsWrapper } from './ProjectShares.styles'

// Hooks
import { useSearch } from 'modules/shares/hooks/useSearch'
import { useSearchFilters } from 'modules/shares/hooks/useSearchFilters'
import { useProject } from 'modules/projects/hooks/useProject/useProject'

// Types
import {
  WorkspaceMinimalFragment,
  useGetDocumentListSettingsQuery,
  ShareListItemFragment,
} from '@sketch/gql-types'

export interface ProjectSharesProps {
  projectId: string
  workspace: WorkspaceMinimalFragment
  userIsEditor: boolean
  userCanRenameProject: boolean
  userCanManageCollection: boolean
}

interface ProjectDocumentListProps {
  projectId: string
  workspace: WorkspaceMinimalFragment
  totalCount: number
  onLoadMore: () => Promise<any>
  shares: ShareListItemFragment[]
}

const ProjectDocumentList: FC<ProjectDocumentListProps> = ({
  workspace,
  projectId,
  totalCount,
  onLoadMore,
  shares,
}) => {
  const { showModal } = useModalContext()
  const isCollectionsFFEnabled = useFlag('collections')

  return (
    <DocumentItemsLayout
      projectId={projectId}
      shares={shares}
      showProjectName={false}
      showCollectionName={isCollectionsFFEnabled}
      totalCount={totalCount}
      onLoadMore={onLoadMore}
      renderDropdown={item => {
        // Rendering a Share Item
        const share = shares.find(share => share.identifier === item.identifier)

        if (!share) {
          ErrorHandler.shouldNeverHappen(
            'share should always be present rendering the ProjectDocumentDropdown'
          )
          return null
        }

        return (
          <DocumentItemDropdown
            share={share}
            workspaceIdentifier={workspace.identifier}
          />
        )
      }}
      action={item => {
        // This action is triggered when the user clicks the DocumentItem
        const isShare = item.__typename === 'Share'
        if (isShare && item.deletedAt) {
          return () => showModal(ShareTrashedModal, { share: item })
        }

        return 'link'
      }}
    />
  )
}

const ProjectShares: FC<ProjectSharesProps> = props => {
  const {
    workspace,
    projectId,
    userIsEditor,
    userCanRenameProject,
    userCanManageCollection,
  } = props
  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionsFFEnabled = useFlag('collections')
  const location = useLocation()
  const { searchDebounced: search } = useSearch()
  const { filters } = useSearchFilters()

  const { identifier: workspaceId } = workspace

  const { data, loading, error, loadMore } = useProject({
    workspaceId,
    projectId,
    search,
    filters,
  })

  // Load the document layout
  const { data: documentListSettingsData } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = documentListSettingsData || {}

  const isCurrentlyInProjectView =
    location.pathname ===
    routes.WORKSPACE_PROJECT.create({ workspaceId, projectId })

  const isCurrentlyInTrashProjectView =
    location.pathname ===
    routes.WORKSPACE_TRASH_PROJECT.create({ workspaceId, projectId })

  const canUserAdministerShares = getCanAdministerShares(workspace)

  if (loading) {
    return <LoadingState />
  }

  if (error) {
    return (
      <ProjectCollectionError
        projectId={projectId}
        error={error!}
        type="project"
      />
    )
  }

  const project = data.project
  const collections = data.collections?.entries ?? []
  const isDraftsProject = project?.type === 'PERSONAL_DRAFTS'

  const shares = project?.shares.entries ?? []
  const hasCollections = !!collections.length
  const hasShares = !!shares.length

  const totalCount = {
    shares: project?.shares.meta.totalCount ?? 0,
    collections: data.collections?.meta.totalCount ?? 0,
  }

  /* ==========
   * Handle redirects when trashing / restoring projects
   * ==========
   * > Redirect to the Trash Project View if the project has been soft-deleted */

  if (!!project?.deletedAt && isCurrentlyInProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_TRASH_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  /*
   * > Redirect to the Project View if the project was restored */
  if (!project?.deletedAt && isCurrentlyInTrashProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }
  /* ==========
   * ========== */

  // Project Empty State
  if (!loading && shares.length === 0 && collections.length === 0) {
    return (
      <ProjectEmptyState
        workspace={workspace}
        project={project!}
        search={search}
        userIsEditor={userIsEditor}
        userCanRenameProject={userCanRenameProject}
        canUserAdministerShares={canUserAdministerShares}
        userCanManageCollection={userCanManageCollection}
      />
    )
  }

  const renderDocumentsList = () => {
    if (!hasShares) {
      return null
    }

    if (isCollectionsFFEnabled && hasCollections) {
      const isGridLayout = documentsLayout === 'GRID'

      return (
        <DocumentsWrapper listLayout={!isGridLayout}>
          {/**
           * This header should only be seen when the user
           * has both shares and collections
           * and is on the grid view
           */}
          {isGridLayout && (
            <StyledHeader>
              Documents
              <Pill variant="secondary">{totalCount.shares}</Pill>
            </StyledHeader>
          )}
          <ProjectDocumentList
            projectId={projectId}
            workspace={workspace}
            shares={shares}
            totalCount={totalCount.shares}
            onLoadMore={loadMore.project}
          />
        </DocumentsWrapper>
      )
    }

    // return list without gradient wrapper when there's no collections
    return (
      <ProjectDocumentList
        projectId={projectId}
        workspace={workspace}
        shares={shares}
        totalCount={totalCount.shares}
        onLoadMore={loadMore.project}
      />
    )
  }

  return (
    <>
      <DocumentHead title={isDraftsProject ? 'Drafts' : project?.name} />
      {project && (
        <ProjectHeader
          project={project}
          workspace={workspace}
          workspaceAccessLevel={project?.workspaceAccessLevel!}
          userCanRenameAndDeleteProject={userCanRenameProject}
          userCanManageCollection={userCanManageCollection}
        />
      )}
      {isCollectionsFFEnabled && hasCollections && (
        <>
          <CollectionItemsLayout
            workspaceId={workspaceId}
            projectId={projectId}
            collections={collections}
            onLoadMore={loadMore.collections}
            showProjectName={false}
            totalCount={totalCount.collections}
            renderDropdown={item => {
              // Rendering a Collection Item
              const userCanManageCollection =
                userIsEditor || canUserAdministerShares

              const collection = collections.find(
                (collection: any) => collection.identifier === item.identifier
              )

              if (!collection) {
                ErrorHandler.shouldNeverHappen(
                  'share should always be present rendering the ProjectDocumentDropdown'
                )
                return null
              }

              return (
                <CollectionDropdown
                  userCanManageCollection={userCanManageCollection}
                  workspace={workspace}
                  collection={collection}
                  project={project!}
                  collections={collections}
                />
              )
            }}
          />
        </>
      )}

      {renderDocumentsList()}
    </>
  )
}

export default ProjectShares
